import React, { useLayoutEffect, useState } from "react";
import { Sun, Moon, SunMoon, Minus, Plus, Type } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const SCALES = [90, 100, 110, 120, 130];
const stored = (key) => { try { return localStorage.getItem(key); } catch { return null; } };

export const DisplayControls = () => {
  const [theme, setTheme] = useState(() => { const value = stored("markoo_theme"); return ["light", "dark", "auto"].includes(value) ? value : "dark"; });
  const [scale, setScale] = useState(() => { const value = Number(stored("markoo_font_scale")); return SCALES.includes(value) ? value : 100; });
  useLayoutEffect(() => {
    const root = document.documentElement;
    const previous = { dark: root.classList.contains("dark"), font: root.style.fontSize, mode: root.getAttribute("data-workspace-theme") };
    const deviceTheme = window.matchMedia("(prefers-color-scheme: dark)");
    const applyTheme = () => {
      const resolved = theme === "auto" ? deviceTheme.matches ? "dark" : "light" : theme;
      root.classList.toggle("dark", resolved === "dark");
      root.setAttribute("data-workspace-theme", resolved);
    };
    applyTheme();
    if (theme === "auto") deviceTheme.addEventListener("change", applyTheme);
    root.style.fontSize = `${scale}%`;
    try { localStorage.setItem("markoo_theme", theme); localStorage.setItem("markoo_font_scale", String(scale)); } catch {}
    return () => {
      deviceTheme.removeEventListener("change", applyTheme);
      root.classList.toggle("dark", previous.dark); root.style.fontSize = previous.font;
      if (previous.mode === null) root.removeAttribute("data-workspace-theme"); else root.setAttribute("data-workspace-theme", previous.mode);
    };
  }, [theme, scale]);
  const themes = [
    { id: "day", value: "light", label: "Giorno", Icon: Sun },
    { id: "night", value: "dark", label: "Notte", Icon: Moon },
    { id: "auto", value: "auto", label: "Automatico", Icon: SunMoon },
  ];
  const Current = (themes.find((t) => t.value === theme) || themes[1]).Icon;
  return <Popover>
    <PopoverTrigger asChild>
      <Button type="button" variant="outline" size="sm" className="gap-1.5 h-9 px-3 text-muted-foreground" title="Aspetto e dimensione testo" aria-label="Aspetto e dimensione testo" data-testid="display-controls"><Current className="w-4 h-4" /><Type className="w-4 h-4" /></Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-72 space-y-4" data-testid="display-panel">
      <div>
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Aspetto</p>
        <div className="grid grid-cols-3 gap-2">{themes.map(({ id, value, label, Icon }) => <button key={id} type="button" onClick={() => setTheme(value)} aria-pressed={theme === value} className={`flex flex-col items-center gap-1.5 rounded-lg border py-3 text-xs transition-colors ${theme === value ? "border-primary bg-primary/10 text-primary ring-1 ring-primary" : "border-border hover:bg-secondary"}`} data-testid={`display-${id}`}><Icon className="w-4 h-4" />{label}</button>)}</div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Dimensione testo</p>
        <div className="flex items-center justify-between">
          <Button type="button" variant="outline" size="icon" disabled={scale === 90} onClick={() => setScale((v) => Math.max(90, v - 10))} aria-label="Riduci carattere" data-testid="display-font-decrease"><Minus className="w-4 h-4" /></Button>
          <output className="font-mono text-sm" aria-live="polite" data-testid="display-font-value">{scale}%</output>
          <Button type="button" variant="outline" size="icon" disabled={scale === 130} onClick={() => setScale((v) => Math.min(130, v + 10))} aria-label="Aumenta carattere" data-testid="display-font-increase"><Plus className="w-4 h-4" /></Button>
        </div>
      </div>
    </PopoverContent>
  </Popover>;
};
